import { useMemo, useState } from 'react';
import { MediaUserItem } from '@/types/unFollow';
import { User } from 'firebase/auth';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'next-i18next';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import GenderDistribution from '@/components/RecentFollow/GenderDistribution';
import LockedWrapper from '@/components/Report/LockedWrapper';
import Link from 'next/link';
import { Tooltip } from 'antd';
import { QuestionCircleFilled } from '@ant-design/icons';
import { imgUrl, proxyImgUrl } from '@/shared/utils';

// 扩展 MediaUserItem 类型，添加 is_verified 属性
interface ExtendedMediaUserItem extends MediaUserItem {
  is_verified?: boolean;
}

interface UnfollowResultProps {
  result: MediaUserItem;
  currentUser: User | null;
  onLoadMore: () => void;
}

const UnfollowResult = ({ result, currentUser, onLoadMore }: UnfollowResultProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'not-back' | 'mutual' | 'unfollow'>('not-back');

  // 根据 activeTab 筛选数据
  const filteredData = useMemo(() => {
    if (!result?.following_list?.length) return [];

    if (activeTab === 'not-back') {
      return result.following_list.filter((user) => !user.has_follow_back) as ExtendedMediaUserItem[];
    } else if (activeTab === 'mutual') {
      return result.following_list.filter((user) => user.has_follow_back) as ExtendedMediaUserItem[];
    } else {
      return []; // Unfollow 标签页始终返回空数组，以触发模拟数据显示
    }
  }, [result?.following_list, activeTab]);

  // 根据标签页类型生成相应的模拟数据
  const mockData = useMemo(() => {
    const count = 5;
    return Array(count)
      .fill(null)
      .map((_, i) => {
        // 根据不同标签页生成不同名称
        let userName = '';
        if (activeTab === 'unfollow') {
          userName = `left_user_${i}`;
        } else if (activeTab === 'mutual') {
          userName = `mutual_user_${i}`;
        } else {
          userName = `not_back_user_${i}`;
        }

        return {
          media_name: userName,
          full_name: i % 2 === 0 ? `${activeTab}_user_${i}` : '-',
          media_avatar: result?.media_avatar || '',
          has_follow_back: activeTab === 'mutual',
          is_verified: i === 0 || i === 2,
        };
      }) as ExtendedMediaUserItem[];
  }, [activeTab, result?.media_avatar]);

  // 确定是否应该锁定内容
  const isLocked = useMemo(() => {
    // 无数据的情况
    if (!result?.following_list?.length) return true;

    // unfollow 标签页始终锁定
    if (activeTab === 'unfollow') return true;

    // 筛选后数据为空的情况
    if (filteredData.length === 0) return true;

    return false;
  }, [activeTab, result?.following_list, filteredData]);

  // 自定义锁定提示文案
  const tabUnlockTips = useMemo(() => {
    if (activeTab === 'unfollow') {
      return t('Someone hit unfollow. Curious? Upgrade to see who left');
    } else {
      return t('Unlock For More Details');
    }
  }, [activeTab]);

  // 最终显示的数据
  const displayData = useMemo(() => {
    // 筛选数据为空时显示模拟数据
    return filteredData.length > 0 ? filteredData : mockData;
  }, [filteredData, mockData]);

  if (!result) return null;

  return (
    <div className="w-[33.875rem] mt-14 mx-auto max-md:w-full max-md:mt-8">
      {/* 用户信息头部 */}
      <div className="flex items-center justify-center">
        <Link href={`https://www.instagram.com/${result?.media_name}/`} target="_blank">
          <Image
            src={imgUrl(proxyImgUrl(result?.media_avatar), 'small') || '/images/layouts/placeholder-icon.webp'}
            alt={`${result?.media_name}'s avatar`}
            className="w-[68px] h-[68px] rounded-full mr-4 cursor-pointer"
            loading="lazy"
            width={68}
            height={68}
          />
        </Link>
        <div className="text-left">
          <div className="mb-1.5 text-2xl font-semibold">{result?.media_name}</div>
          <div className="flex space-x-4 text-sm text-gray-600">
            <span>
              {result?.follower_count} {t('followers')}
            </span>
            <span>
              {result?.following_count} {t('following')}
            </span>
          </div>
        </div>
      </div>

      {/* 结果显示区域 */}
      <div className="w-full mt-12 mx-auto max-md:mt-8 bg-white rounded-lg shadow-md overflow-hidden">
        {/* 标签页 */}
        <div className="flex border-b text-[16px] leading-5 max-md:rounded-lg max-md:overflow-hidden">
          <div
            className={cn(
              'flex-1 py-3 text-center font-semibold cursor-pointer transition-colors',
              activeTab === 'not-back' ? 'bg-pink-500 text-white' : 'text-gray-600 bg-[#f3f4f6] hover:bg-gray-200'
            )}
            onClick={() => setActiveTab('not-back')}
          >
            <div className="flex justify-center items-center gap-1">
              {t('Not Back')}
              <Tooltip title={t('You can click on any user to explore further on Instagram.')} placement="bottomLeft">
                <QuestionCircleFilled className="text-sm cursor-pointer" />
              </Tooltip>
            </div>
          </div>
          <div
            className={cn(
              'flex-1 py-3 text-center font-semibold cursor-pointer transition-colors',
              activeTab === 'mutual' ? 'bg-green-500 text-white' : 'text-gray-600 bg-[#f3f4f6] hover:bg-gray-200'
            )}
            onClick={() => setActiveTab('mutual')}
          >
            <div className="flex justify-center items-center gap-1">
              {t('Mutual')}
              <Tooltip title={t('You can click on any user to explore further on Instagram.')} placement="bottomLeft">
                <QuestionCircleFilled className="text-sm cursor-pointer" />
              </Tooltip>
            </div>
          </div>
          <div
            className={cn(
              'flex-1 py-3 text-center font-semibold cursor-pointer transition-colors relative',
              activeTab === 'unfollow' ? 'bg-purple-500 text-white' : 'text-gray-600 bg-[#f3f4f6] hover:bg-gray-200'
            )}
            onClick={() => setActiveTab('unfollow')}
          >
            {t('Unfollow')}
          </div>
        </div>

        {/* 内容区域 */}
        <div className="p-8 max-md:p-4">
          {/* 用户列表 */}
          <div className={cn('relative')}>
            {/* 单行样式的用户列表 */}
            <LockedWrapper locked={isLocked} tips={tabUnlockTips} onClick={onLoadMore}>
              <div className="space-y-1">
                {displayData.map((account, index) => (
                  <div
                    key={index}
                    className="flex items-center p-2 cursor-pointer hover:bg-gray-50 transition-colors group"
                    onClick={() => {
                      window.open(`https://www.instagram.com/${account?.media_name}/`, '_blank');
                    }}
                  >
                    <div
                      className={cn(
                        'w-8 h-8 flex items-center justify-center rounded-full text-white text-sm font-medium mr-3',
                        index < 3 ? 'bg-purple-500' : 'bg-gray-400'
                      )}
                    >
                      {index + 1}
                    </div>
                    <div className="w-10 h-10 rounded-full overflow-hidden mr-3 flex-shrink-0">
                      <Image
                        src={'/n-api/image-proxy?url=' + encodeURIComponent(account?.media_avatar ?? '')}
                        alt={`${account?.media_name}'s avatar`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        width={40}
                        height={40}
                      />
                    </div>
                    <div className="flex-grow min-w-0">
                      <div className="flex items-center">
                        <p className="text-[1rem] leading-5 font-medium text-gray-800 truncate">
                          @{account?.media_name}
                        </p>
                        {account.is_verified && (
                          <Image
                            src={'/images/follow/blue-mark.webp'}
                            alt={'blue-mark'}
                            width={16}
                            height={16}
                            className="ml-1"
                          />
                        )}
                        {account.is_private && (
                          <Tooltip
                            title={t('This account is private, so follow data isn’t available and it may appear here.')}
                          >
                            <div className="text-sm leading-[1] px-2 py-1 ml-3 rounded-md text-[#F04444] border border-[#FFD1D1] bg-[#FFE9E9]">
                              Private
                            </div>
                          </Tooltip>
                        )}
                      </div>
                      <p className="mt-1 text-[0.875rem] leading-4 text-gray-500 text-left truncate">
                        {account?.full_name || '-'}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                ))}
              </div>
            </LockedWrapper>
          </div>

          {/* 加载更多指示器 */}
          <div className="mt-6 mb-8 flex justify-center items-center space-x-2 cursor-pointer" onClick={onLoadMore}>
            <span className="w-2 h-2 bg-gray-300 rounded-full hover:bg-gray-400 transition-colors animate-pulse"></span>
            <span className="w-2 h-2 bg-gray-300 rounded-full hover:bg-gray-400 transition-colors animate-pulse delay-100"></span>
            <span className="w-2 h-2 bg-gray-300 rounded-full hover:bg-gray-400 transition-colors animate-pulse delay-200"></span>
          </div>

          {/* 性别分布部分 */}
          <GenderDistribution
            male={result?.gender_info?.boys}
            female={result?.gender_info?.girls}
            other={result?.gender_info?.others}
            onClick={onLoadMore}
          />

          {/* 加载更多按钮 */}
          <div className="mt-6 text-center">
            <p className="text-[#6b7280] mt-6 text-[1rem] leading-5 max-md:mt-[1.125rem] max-md:text-[calc(0.875rem-1px)] max-md:leading-4">
              {t('Get complete insights with DolphinRadar Analytics')}
            </p>
            <button
              className="bg-pink-500 hover:bg-pink-600 text-white font-semibold mt-8 mb-4 py-3 px-6 max-md:mt-4 max-md:mb-2 rounded-md transition-colors w-full max-w-xs mx-auto"
              onClick={onLoadMore}
            >
              {t('Load More Truth')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnfollowResult;
